import React, { useState } from 'react';
import styled from 'theme/styled';
import Avatar from '@mui/material/Avatar';
import useMediaQuery from '@mui/material/useMediaQuery';
import { NavLink } from 'react-router-dom';
import { appsWithAccess } from 'apps/available';
import { useAppAccess } from 'core/data/app-access';
import { App } from 'core/types/app';
import { Container } from '../layout';
import NavigationMobileMenu from './submenu-mobile';
import NavigationProfileMenu from './profile-menu';

const mobileBreakpoint = 650;

interface Props {
  active: App;
}

export default function MainNavigationHeader({ active }: Props): JSX.Element {
  const access = useAppAccess();
  const [open, setOpen] = useState<boolean>(false);
  const mobile = useMediaQuery(`(max-width: ${mobileBreakpoint}px)`);

  const apps = appsWithAccess(access);

  return (
    <Header>
      <Container
        margins={0}
        direction='row'
        alignItems='center'
        justifyContent='space-between'
      >
        {open ? (
          <div />
        ) : (
          <nav aria-labelledby='primary-navigation'>
            <AppIconNavLink to={`/app/${active.id}/`}>
              <AppIcon variant='rounded' className='active'>
                <img src={active.icon || ''} alt='' />
              </AppIcon>
            </AppIconNavLink>
            <ol>
              {apps.map(
                app =>
                  access[app.id] && (
                    <li
                      key={app.id}
                      className={`app-link ${active.id === app.id ? 'active' : ''}`}
                    >
                      <NavLink to={`/app/${app.id}/`}>
                        <h2>{app.name}</h2>
                      </NavLink>
                    </li>
                  ),
              )}
            </ol>
          </nav>
        )}

        {mobile ? (
          <NavigationMobileMenu
            open={open}
            setOpen={setOpen}
            active={active.id}
            apps={apps}
          />
        ) : (
          <NavigationProfileMenu />
        )}
      </Container>
    </Header>
  );
}

const Header = styled.header`
  background-color: ${({ theme }) => theme.palette.primary.light};
  width: 100%;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: space-around;

  nav {
    display: flex;
    align-items: center;
  }

  .app-link {
    margin: 0;

    &.active {
      display: block;

      a {
        color: ${({ theme }) => theme.palette.background.paper};
      }
    }

    h2 {
      padding: 0 ${({ theme }) => theme.spacing(2)};
    }

    &:first-child {
      h2 {
        padding: 0 ${({ theme }) => theme.spacing(2)} 0 0;
      }
    }

    a {
      color: ${({ theme }) => theme.palette.primary.dark};
      text-decoration: none;

      :hover {
        color: ${({ theme }) => theme.palette.background.paper};
      }
    }

    @media (max-width: ${mobileBreakpoint}px) {
      display: none;
    }
  }

  .app-icon {
    background-color: ${({ theme }) => theme.palette.primary.main};
  }

  ol {
    padding: 0;
    margin: 0;
    display: flex;

    li {
      list-style: none;
    }
  }
`;

const AppIconNavLink = styled(NavLink)`
  @media (min-width: 1150px) {
    position: absolute !important;
    left: -80px;
  }
`;

const AppIcon = styled(Avatar)`
  background-color: ${({ theme }) => theme.palette.common.black};
  margin-right: ${({ theme }) => theme.spacing(3)};

  &.active {
    background-color: ${({ theme }) => theme.palette.primary.main};
  }
`;
